// JSX port of careplan-render.js (window.CarePlanRender.render) — the collapsible
// "Your Plan" accordion: Care Goal · Medications · Daily Playbook · Your Devices ·
// Care Pathway. Native <details>/<summary> accordions (no imperative DOM). All text
// is auto-escaped JSX (NO dangerouslySetInnerHTML). NUMERIC coerced via num().
// A non-active (draft / pre-alignment) plan renders the "being prepared" placeholder.

import React from 'react';
import { num, kgToLb, weightDisplayToLb } from '@/logic/patientHome';
import { Icon } from './icons';
import {
  foldedPlanTargets, isAtPlanTarget, planTargetNum, planTargetText,
} from '../../../logic/carePlanTargets';

const goalLabels = {
  bp_systolic: 'Systolic BP', bp_diastolic: 'Diastolic BP', weight: 'Weight', hba1c: 'HbA1c',
  bmi: 'BMI', ldl_c: 'LDL-C', egfr: 'eGFR', uacr: 'uACR', steps_daily: 'Steps',
};
const goalUnits = {
  bp_systolic: 'mmHg', bp_diastolic: 'mmHg', weight: 'lb', hba1c: '%', bmi: '',
  ldl_c: 'mg/dL', egfr: 'mL/min', uacr: 'mg/g', steps_daily: '',
};
const higherIsBetter = { egfr: true, steps_daily: true };

const pathwayLabels = {
  htn: { label: 'Hypertension', guideline: 'ACC/AHA 2017' },
  lipid: { label: 'High Cholesterol', guideline: 'AHA/ACC 2018' },
  obesity: { label: 'Weight Management', guideline: 'AHA/ACC/TOS 2013' },
  prediabetes: { label: 'Prediabetes', guideline: 'ADA 2025' },
  t2d: { label: 'Type 2 Diabetes', guideline: 'ADA 2025' },
  ckd: { label: 'Kidney Health (CKD)', guideline: 'KDIGO 2024' },
  ascvd: { label: 'Cardiovascular Disease', guideline: 'AHA/ACC 2019' },
  ckm: { label: 'Cardio-Kidney-Metabolic Care', guideline: 'AHA 2023' },
  'ascvd-t2d': { label: 'Heart + Diabetes Care', guideline: 'ADA/AHA 2025' },
  'obesity-t2d': { label: 'Weight + Diabetes Care', guideline: 'ADA 2025' },
};
const deviceMeta = {
  'BPM Pro 2': { icon: '❤️', desc: 'Tracks your blood pressure' },
  'BPM Connect': { icon: '❤️', desc: 'Tracks your blood pressure' },
  'Body Pro 2': { icon: '⚖️', desc: 'Tracks your weight & body composition' },
  'Body Pro': { icon: '⚖️', desc: 'Tracks your weight & body composition' },
  'Body Comp': { icon: '⚖️', desc: 'Tracks your weight & body composition' },
  'Body Smart': { icon: '⚖️', desc: 'Tracks your weight' },
  Thermo: { icon: '🌡️', desc: 'Tracks your temperature' },
  'Sleep Analyzer': { icon: '🛌', desc: 'Tracks your sleep quality' },
  ScanWatch: { icon: '⌚', desc: 'Tracks your activity & vitals' },
};
const cadenceMeta = {
  bp: { icon: '❤️', label: 'blood pressure' },
  weight: { icon: '⚖️', label: 'weight' },
  glucose: { icon: '🩸', label: 'blood glucose' },
  temperature: { icon: '🌡️', label: 'temperature' },
  spo2: { icon: '🫁', label: 'SpO2' },
  hba1c: { icon: '🧪', label: 'HbA1c' },
};

function getGoalTargetNumeric(goals, measureType, fallback = null) {
  const goal = (goals || []).find((g) => g.measure_type === measureType);
  if (!goal) return fallback;
  if (goal.target_value_num != null) return num(goal.target_value_num);
  // A %-shaped weight text ('5-10% reduction') is a percent — never a weight target.
  if (goal.target_value_text && !(measureType === 'weight' && /%/.test(goal.target_value_text))) {
    const m = goal.target_value_text.match(/[\d.]+/);
    if (m) return parseFloat(m[0]);
  }
  return fallback;
}

function goalProgress(g) {
  // NOT care_goal.status. That stored verdict is written by two different workflow nodes with
  // two different meanings — one sets 'on_target' from the mere presence of a reading, with no
  // target comparison at all — and trusting it short-circuited this bar to 100% before any
  // arithmetic ran. So a too-lenient cached target showed a full bar under an out-of-range
  // number. Computed from the authored target instead; falls back to the stored flag only when
  // the plan cannot answer.
  if (g._at_plan_target === true) return 100;
  const st = (g.status || '').toLowerCase();
  if (g._at_plan_target == null && (st === 'on_target' || st === 'achieved')) return 100;
  const baseline = num(g.baseline_value_num);
  const current = num(g.last_value_num);
  const target = num(g.target_value_num != null ? g.target_value_num : getGoalTargetNumeric([g], g.measure_type));
  if (baseline == null || current == null || target == null) return 0;
  const lower = !higherIsBetter[g.measure_type];
  if (lower ? current <= target : current >= target) return 100;
  const moved = lower ? current < baseline : current > baseline;
  if (!moved) return 0;
  const range = Math.abs(baseline - target);
  return range > 0 ? Math.round(Math.min(1, Math.abs(baseline - current) / range) * 100) : 0;
}


// The card answers one question — is the plan's target met — so the verdict is COMPUTED against
// the authored target. care_goal.status is never consulted: two workflow nodes write it with two
// meanings, one of them setting 'on_target' from the mere presence of a reading.
function resolveVerdict(g) {
  if (g._at_plan_target === true) return 'on_target';
  if (g._at_plan_target === false) return 'off_target';
  // No verdict is possible two different ways, and they are not the same thing to a reader.
  if (g.last_value_num == null) return 'no_reading';
  return 'tracking'; // readings exist; a reduce_by_pct target has no absolute to score against
}

const badgeMap = {
  on_target: { cls: 'on-target', label: 'On target' },
  off_target: { cls: 'needs-baseline', label: 'Not at target' },
  tracking: { cls: 'tracking', label: 'Tracking' },
  no_reading: { cls: 'no-data', label: 'No reading yet' },
};
function Badge({ status }) {
  const b = badgeMap[status] || badgeMap.no_reading;
  return <span className={`acc-goal-badge acc-goal-badge--${b.cls}`}>{b.label}</span>;
}
function barColor(status) {
  switch (status) {
    case 'on_target': return 'var(--acc-green)';
    case 'tracking': return 'var(--acc-teal)';
    case 'off_target': return 'var(--acc-amber)';
    default: return 'var(--text-dim50)';
  }
}
// One decimal, never rounded away: HbA1c 7.2 displayed as "7" against a <7% target reads as met.
function fmtGoalVal(measureType, v) {
  const n = num(v);
  if (n == null) return '—';
  if (measureType === 'weight') return n.toFixed(1);
  return String(Math.round(n * 10) / 10);
}

const OP_SYMBOL = { '<=': '≤', '<': '<', '>=': '≥', '>': '>', '=': '' };

// The card's headline is the TARGET, so it must read as one — composed from the operator and the
// value, not from the plan's prose (`<130/80 mmHg` is the pair, and would print on both BP cards).
function heroTarget(g) {
  const unit = goalUnits[g.measure_type] || '';
  if (g.target_operator === 'reduce_by_pct') {
    const pct = num(g._target_value_raw);
    return pct == null ? null : { value: `↓ ${fmtGoalVal('pct', pct)}%`, unit: '' };
  }
  const v = num(g.target_value_num);
  if (v == null) return null;
  const op = OP_SYMBOL[g.target_operator] ?? '';
  const head = `${op ? `${op} ` : ''}${fmtGoalVal(g.measure_type, v)}`;
  // '%' sits tight against its number; a spaced unit chip is for mmHg / mg/dL / lb.
  return unit === '%' ? { value: `${head}%`, unit: '' } : { value: head, unit };
}

// (1) The plan's prose earns its footnote only when it says something the composed headline does
// not. '<130/80 mmHg' under '≤ 130 mmHg' is noise — and worse, it is the PAIR, so it repeats
// identically on both BP cards. Keep it when real words survive stripping the maths and the unit.
const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
function qualifierNote(measureType, text) {
  if (!text) return '';
  const unit = goalUnits[measureType] || '';
  const bare = (unit ? text.replace(new RegExp(unit.replace(RE_ESCAPE, '\\$&'), 'gi'), ' ') : text)
    .replace(/[0-9.,/<>=≤≥%()+-]/g, ' ')
    .trim();
  return bare ? text : '';
}

// Build the goal cards from the PLAN + measure_states. care_goal is NOT read (retired
// 2026-08-26): the plan says which goals exist and what the target is, measure_state says
// where the patient is. Mirrors sparks/access-patient/js/careplan-render.js.
function buildContext(plan, measureStates) {
  if (!plan) return { plan: null, goals: [], planTargets: {}, mStateMap: {} };
  // Folded into the MEASURE vocabulary: the plan authors `a1c` while every measure consumer
  // says `hba1c`, so a raw lookup missed it — and the un-folded key also rendered as its own
  // raw "a1c" tile beside the patient's real HbA1c goal.
  const planTargets = Array.isArray(plan.targets)
    ? Object.fromEntries(plan.targets.map((t) => [t.measure_type, t]))
    : foldedPlanTargets(plan.targets || {});

  const mStateMap = Object.fromEntries((measureStates || []).map((s) => [s.measure_type, s]));

  // Every authored plan target, plus any measure already tracked that is a recognised goal.
  // `goalLabels` is the filter — a measure with no label (e.g. `height`) is an input to a
  // goal, not a goal. A tracked measure with no plan target still gets a card so the readings
  // stay visible; it simply carries no target claim, because no human authored one.
  // The care plan authors the goals — nothing else. A measure with a measure_state row but no
  // authored target (eGFR/uACR on a CKM plan) is something we TRACK, not something we aim at, and
  // showing it here manufactured a goal the clinician never set.
  const types = Object.keys(planTargets);

  const goals = types.map((mt) => {
    const ms = mStateMap[mt] || null;
    return {
      measure_type: mt,
      target_value_num: planTargetNum(plan.targets, mt),
      target_value_text: planTargetText(plan.targets, mt),
      target_operator: (planTargets[mt] && planTargets[mt].operator) || null,
      _target_value_raw: (planTargets[mt] && planTargets[mt].value) != null ? planTargets[mt].value : null,
      baseline_value_num: ms && ms.baseline_value != null ? ms.baseline_value : null,
      last_value_num: ms && ms.latest_value != null ? ms.latest_value : null,
      _ms_status: ms ? ms.status : null,
      // Only a CMS-filed baseline may be shown under the word "Baseline". WF9 rolls
      // baseline_value on every reading until WF37 stamps 'reportable' at submission, so an
      // ungated render showed the patient a number that silently moved. The value still feeds
      // the progress bar as a comparison point (logic/baseline.ts comparisonBaseline).
      _baseline_locked: !!ms && String(ms.status) === 'reportable',
      _ms_total_readings: ms ? ms.total_readings : null,
    };
  });

  // Stamp the computed verdict while everything is still in the TARGET's own unit (kg for
  // weight) — comparing a lb reading to a kg target would invert the answer.
  goals.forEach((g) => {
    g._at_plan_target = isAtPlanTarget(plan.targets, g.measure_type, g.last_value_num);
  });

  // US display: weight renders in lb. Convert AFTER everything above, and only into COPIES —
  // measure_states rows are shared props (WeightChart classifies kg history against them).
  const wAbsolute = goals.some((g) => g.measure_type === 'weight' && g.target_value_num != null);
  const lbGoals = goals.map((g) => (g.measure_type !== 'weight' ? g : {
    ...g,
    last_value_num: kgToLb(g.last_value_num),
    baseline_value_num: kgToLb(g.baseline_value_num),
    target_value_num: wAbsolute ? kgToLb(g.target_value_num) : null,
  }));
  if (mStateMap.weight) {
    mStateMap.weight = {
      ...mStateMap.weight,
      latest_value: kgToLb(mStateMap.weight.latest_value),
      baseline_value: kgToLb(mStateMap.weight.baseline_value),
    };
  }

  return { plan, goals: lbGoals, planTargets, mStateMap };
}

// planTargets/mStateMap are no longer needed here: every card carries its own authored
// target and its merged values from buildContext, through ONE render path.
function GoalsBody({ goals }) {
  if (!goals.length) return null;

  // Two different truths, and the unfiled one wins: a baseline that exists but has not been
  // filed (number withheld, progress provisional) vs a measure with no starting value at all.
  const hasUnfiledBaseline = goals.some((g) => g.baseline_value_num != null && !g._baseline_locked);
  const hasNoBaseline = goals.some((g) => g.baseline_value_num == null);

  return (
    <>
      {hasUnfiledBaseline ? (
        <div className="acc-plan-hint">Baseline is still being confirmed with the care team. Progress shown here is provisional.</div>
      ) : hasNoBaseline ? (
        <div className="acc-plan-hint">Starting values are being established from the first readings. Progress tracking begins once the baseline is set.</div>
      ) : null}
      <div className="acc-goals-track">
        {goals.map((g) => {
          // Card order is the question the reader is asking: what is the target, am I meeting it,
          // what did I start from. The latest reading is NOT printed — it is a chart's job, and
          // as the headline it left four of eight cards leading with an em dash.
          const status = resolveVerdict(g);
          const hero = heroTarget(g);
          const baseline = g.baseline_value_num;
          const unit = goalUnits[g.measure_type] || '';
          const label = goalLabels[g.measure_type] || g.measure_type;
          const filed = baseline != null && g._baseline_locked;
          // A bar needs all three of baseline, reading and target — otherwise it is a 0% rail
          // that reads as "no progress" when the truth is "not measurable".
          const scored = g._at_plan_target != null && baseline != null;
          // The plan's own prose, kept as the footnote: it carries the clinical qualifier
          // ("secondary prev", "DPP") that the composed headline drops.
          const qualifier = qualifierNote(g.measure_type, g.measure_type === 'weight'
            ? weightDisplayToLb(g.target_value_text || '', g.target_operator)
            : (g.target_value_text || ''));
          return (
            <div className="acc-goal-card" key={g.measure_type}>
              <div className="acc-goal-label">{label}</div>
              <div className="acc-goal-metric">
                <span className={`acc-goal-metric__value${hero ? '' : ' acc-goal-metric__value--empty'}`}>{hero ? hero.value : 'No target'}</span>
                {hero && hero.unit && <span className="acc-goal-metric__unit">{hero.unit}</span>}
              </div>
              <Badge status={status} />
              {scored ? (
                <div className="acc-goal-bar"><div className="acc-goal-bar__fill" style={{ width: `${goalProgress(g)}%`, background: barColor(status) }} /></div>
              ) : (
                <div className="acc-goal-bar acc-goal-bar--none" />
              )}
              <div className={`acc-goal-baseline${filed ? '' : ' acc-goal-baseline--none'}`}>
                {filed ? `Baseline ${fmtGoalVal(g.measure_type, baseline)}${unit ? ` ${unit}` : ''}` : 'Baseline not submitted'}
              </div>
              {qualifier && <div className="acc-goal-target">{qualifier}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

function buildPlaybookItems(plan) {
  const cadence = plan.monitoring_cadence || {};
  return Object.entries(cadence).map(([key, cfg]) => {
    const meta = cadenceMeta[key] || { icon: '✓', label: key };
    let text = `Measure ${meta.label}`;
    if (cfg.times_per_week) text += cfg.times_per_week === 7 ? ' daily' : ` ${cfg.times_per_week}x/week`;
    else if (cfg.times_per_year) text += ` ${cfg.times_per_year}x/year`;
    if (cfg.time_of_day && cfg.time_of_day.length) text += `, ${cfg.time_of_day.join(' & ')}`;
    return { key, icon: meta.icon, text };
  });
}

function PlaybookBody({ plan }) {
  const playItems = buildPlaybookItems(plan);
  const exPlan = plan.exercise_plan;
  const nuPlan = plan.nutrition_plan;
  if (!playItems.length && !exPlan && !nuPlan) return null;

  const exParts = [];
  if (exPlan) {
    if (exPlan.minutes_per_day) exParts.push(`${exPlan.minutes_per_day} min/day`);
    if (exPlan.days_per_week) exParts.push(`${exPlan.days_per_week} days/week`);
    if (exPlan.intensity) exParts.push(`${exPlan.intensity} intensity`);
  }
  const exTitle = exPlan?.type ? exPlan.type.charAt(0).toUpperCase() + exPlan.type.slice(1) : 'Exercise';
  const pattern = nuPlan?.pattern || nuPlan?.type || 'Balanced';
  const sodium = nuPlan?.sodium_limit_mg || nuPlan?.sodium_mg_daily;

  return (
    <>
      <div className="acc-plan-sub-hint">Daily routine based on the care plan</div>
      <div className="acc-playbook-list">
        {playItems.map((item) => (
          <div className="acc-playbook-item" key={item.key}>
            <div className="acc-playbook-item__icon">{item.icon}</div>
            <div className="acc-playbook-item__text">{item.text}</div>
          </div>
        ))}
        {exPlan && (
          <div className="acc-playbook-item">
            <div className="acc-playbook-item__icon">🏃</div>
            <div>
              <div className="acc-playbook-item__text">{exTitle}</div>
              {exParts.length > 0 && <div className="acc-playbook-item__sub">{exParts.join(' · ')}</div>}
              {exPlan.restrictions && <div className="acc-playbook-item__sub">{exPlan.restrictions}</div>}
            </div>
          </div>
        )}
        {nuPlan && (
          <div className="acc-playbook-item">
            <div className="acc-playbook-item__icon">🥗</div>
            <div>
              <div className="acc-playbook-item__text">{pattern} Diet</div>
              {sodium && <div className="acc-playbook-item__sub">Sodium: &lt; {sodium}mg/day</div>}
              {nuPlan.special_notes && <div className="acc-playbook-item__sub">{nuPlan.special_notes}</div>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function MedicationsBody({ plan }) {
  const meds = plan.medications || [];
  if (!meds.length) {
    return (
      <div className="acc-med-empty">
        <span className="acc-med-empty__icon">💊</span>
        <span className="acc-med-empty__text">No medications on this care plan. The care team will update this if anything changes.</span>
      </div>
    );
  }
  return (
    <div className="acc-med-list">
      {meds.map((med, i) => (
        <div className="acc-med" key={med.name ? `${med.name}-${med.dose || ''}-${med.frequency || ''}` : `med-${i}`}>
          <div className="acc-med__icon">💊</div>
          <div>
            <div className="acc-med__name">{med.name}{med.dose ? ` ${med.dose}` : ''}</div>
            <div className="acc-med__dose">{med.frequency || ''}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DevicesBody({ plan }) {
  const devices = plan.devices || [];
  if (!devices.length) return null;
  return (
    <div className="acc-device-list">
      {devices.map((d) => {
        const meta = deviceMeta[d] || { icon: '📱', desc: 'Health monitoring device' };
        return (
          <div className="acc-device-item" key={d}>
            <div className="acc-device-item__icon">{meta.icon}</div>
            <div>
              <div className="acc-device-item__name">{d}</div>
              <div className="acc-device-item__desc">{meta.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PathwayBody({ plan }) {
  const conds = plan.conditions || [];
  const pathways = Array.isArray(plan.applied_pathways) ? plan.applied_pathways : [];
  if (!conds.length && !pathways.length) return null;
  return (
    <>
      {conds.length > 0 && (
        <>
          <div className="acc-plan-sub-hint">What we’re caring for</div>
          <div className="acc-conditions">
            {conds.map((c) => <span className="acc-condition-pill" key={c}>{c}</span>)}
          </div>
        </>
      )}
      {pathways.length > 0 && (
        <>
          <div className="acc-plan-sub-hint" style={{ marginTop: 16 }}>Care pathway</div>
          <div className="acc-pathway-list">
            {pathways.map((id) => {
              const meta = pathwayLabels[id] || { label: String(id) };
              return (
                <div className="acc-pathway-item" key={String(id)}>
                  <div className="acc-pathway-item__icon">🧭</div>
                  <div>
                    <div className="acc-pathway-item__name">{meta.label}</div>
                    {meta.guideline && <div className="acc-pathway-item__guideline">Based on {meta.guideline}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function PlanPrep() {
  const steps = [
    { icon: 'news', label: 'Health history & intake' },
    { icon: 'heart-bp', label: 'Baseline labs & first readings' },
    { icon: 'info', label: 'Records shared with the care team' },
  ];
  return (
    <div className="acc-plan-prep">
      <div className="acc-plan-prep__badge"><Icon name="sparkle" size={26} /></div>
      <h3 className="acc-plan-prep__title">The care plan is being prepared</h3>
      <p className="acc-plan-prep__desc">The care team is reviewing everything shared to design a tailored plan. The personalized plan appears here as soon as it’s ready.</p>
      <p className="acc-plan-prep__label">What the care team is reviewing</p>
      <ul className="acc-plan-prep__steps">
        {steps.map((s) => (
          <li className="acc-plan-prep__step" key={s.icon}>
            <span className="acc-plan-prep__step-icon"><Icon name={s.icon} size={16} /></span>
            <span className="acc-plan-prep__step-label">{s.label}</span>
          </li>
        ))}
      </ul>
      <p className="acc-plan-prep__foot">The patient is notified the moment it’s ready — meanwhile, tasks above keep progressing.</p>
    </div>
  );
}

function AccordionItem({ icon, title, count, children }) {
  if (!children) return null;
  return (
    <details className="acc-plan-accordion__item">
      <summary className="acc-plan-accordion__summary">
        <span className="acc-plan-accordion__icon">{icon}</span>
        <span className="acc-plan-accordion__title">{title}</span>
        {count > 0 && <span className="acc-plan-accordion__count">{count}</span>}
        <Icon name="chevron" size={14} className="acc-plan-accordion__chevron icon" />
      </summary>
      <div className="acc-plan-accordion__body">{children}</div>
    </details>
  );
}

export default function YourPlan({ carePlan, measureStates }) {
  const { plan, goals, planTargets } = buildContext(carePlan, measureStates);

  // Personalized plan surfaces only once the care team has finalized (activated) it.
  const content = (!plan || plan.status !== 'active')
    ? <PlanPrep />
    : (
      <div className="acc-plan-accordion">
        <AccordionItem icon="🎯" title="Care Goal" count={goals.length + Object.keys(planTargets).filter((t) => !goals.some((g) => g.measure_type === t)).length}>
          <GoalsBody goals={goals} />
        </AccordionItem>
        <AccordionItem icon="💊" title="Medications" count={(plan.medications || []).length}>
          <MedicationsBody plan={plan} />
        </AccordionItem>
        <AccordionItem icon="🗓️" title="Daily Playbook" count={buildPlaybookItems(plan).length + (plan.exercise_plan ? 1 : 0) + (plan.nutrition_plan ? 1 : 0)}>
          <PlaybookBody plan={plan} />
        </AccordionItem>
        <AccordionItem icon="📡" title="Your Devices" count={(plan.devices || []).length}>
          <DevicesBody plan={plan} />
        </AccordionItem>
        <AccordionItem icon="🧭" title="Care Pathway" count={(Array.isArray(plan.applied_pathways) ? plan.applied_pathways.length : 0) || (plan.conditions || []).length}>
          <PathwayBody plan={plan} />
        </AccordionItem>
      </div>
    );

  return (
    <section className="acc-section fade-up" aria-labelledby="ph-plan-title">
      <div className="acc-section__header">
        <h2 className="acc-section__title" id="ph-plan-title">Your Plan</h2>
      </div>
      <div>{content}</div>
    </section>
  );
}
